/**
 * Shared table-order helpers.
 *
 * The Timeline view lets staff manually reorder tables/combinées; the order
 * is persisted per-establishment (Phase 1: localStorage, Phase 2: DB column
 * `display_order`). These helpers are reused across every staff-side picker
 * (AvailabilityDialog, EditReservationDialog, WalkInDialog) so the order
 * stays consistent everywhere.
 *
 * Behavior:
 * - If no custom order is configured → items are returned via `fallbackSort`
 *   (current behavior, e.g. capacity ASC).
 * - If a custom order exists → items present in the order appear first, in
 *   that order; items missing from it are appended via `fallbackSort`.
 */

const STORAGE_KEY_PREFIX = "timeline-table-order-";

export function getTableOrderStorageKey(establishmentId: string): string {
  return `${STORAGE_KEY_PREFIX}${establishmentId}`;
}

export function loadCustomTableOrder(
  establishmentId: string | undefined | null
): string[] | null {
  if (!establishmentId) return null;
  try {
    const raw = localStorage.getItem(getTableOrderStorageKey(establishmentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((x) => typeof x === "string")
      ? (parsed as string[])
      : null;
  } catch {
    return null;
  }
}

export function saveCustomTableOrder(
  establishmentId: string,
  order: string[]
): void {
  try {
    localStorage.setItem(
      getTableOrderStorageKey(establishmentId),
      JSON.stringify(order)
    );
  } catch {
    // localStorage full / unavailable → ignore silently
  }
}

export function clearCustomTableOrder(establishmentId: string): void {
  try {
    localStorage.removeItem(getTableOrderStorageKey(establishmentId));
  } catch {
    // ignore
  }
}

/**
 * Sort items by a previously-saved custom order.
 *
 * - `items` is NOT mutated (returns a new array).
 * - `customOrder` null/empty → identical to `[...items].sort(fallbackSort)`.
 * - Items in `customOrder` are placed first in that exact sequence.
 * - Items absent from `customOrder` are appended, sorted via `fallbackSort`.
 */
export function sortByCustomOrder<T extends { id: string }>(
  items: T[],
  customOrder: string[] | null | undefined,
  fallbackSort: (a: T, b: T) => number
): T[] {
  if (!customOrder || customOrder.length === 0) {
    return [...items].sort(fallbackSort);
  }

  const orderMap = new Map<string, number>();
  customOrder.forEach((id, i) => orderMap.set(id, i));

  const inOrder: T[] = [];
  const notInOrder: T[] = [];
  for (const it of items) {
    if (orderMap.has(it.id)) inOrder.push(it);
    else notInOrder.push(it);
  }
  inOrder.sort((a, b) => (orderMap.get(a.id) ?? 0) - (orderMap.get(b.id) ?? 0));
  notInOrder.sort(fallbackSort);

  return [...inOrder, ...notInOrder];
}

/**
 * Build the effective custom order from DB `display_order` fields.
 * Returns null if no item carries a non-null display_order.
 */
export function deriveDbTableOrder(
  items: Array<{ id: string; displayOrder?: number | null }>
): string[] | null {
  const withOrder = items.filter(
    (i) => i.displayOrder !== null && i.displayOrder !== undefined
  );
  if (withOrder.length === 0) return null;
  return [...withOrder]
    .sort((a, b) => (a.displayOrder as number) - (b.displayOrder as number))
    .map((i) => i.id);
}

/**
 * Resolve the effective custom order to apply.
 *
 * Priority: DB (`display_order`) > localStorage > null (= use fallback sort).
 *
 * Pass tables AND combinedTables so a single source of truth drives both
 * staff-side pickers and Timeline ordering.
 */
export function resolveTableOrder(args: {
  establishmentId: string | null | undefined;
  tables?: Array<{ id: string; displayOrder?: number | null }>;
  combinedTables?: Array<{ id: string; displayOrder?: number | null }>;
}): string[] | null {
  const combined = [...(args.tables ?? []), ...(args.combinedTables ?? [])];
  const dbOrder = deriveDbTableOrder(combined);
  if (dbOrder) return dbOrder;
  return loadCustomTableOrder(args.establishmentId);
}

